import { useDataProvider } from 'react-admin';
import { useEffect, useState } from "react";
import InputFieldCustom from '../Commons/InputFieldCustom'
import { useMediaQuery, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import BreadCrumbCustom from '../Commons/BreadCrum';
import historyTracker from '../Commons/historyTracker';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import AuthNotification from '../Commons/AuthNotification';
import { Style } from '../Commons/Styles.jsx';
import BottomToolBar from '../Commons/BottomToolBar';
import InternalMenu from '../Commons/InternalMenu';
import CircularProgress from '@mui/material/CircularProgress';
// TODO: reemplazar por el ícono definitivo de Proveedores cuando esté disponible
import providers from '../Commons/Icons/apps.svg';
import providerList from '../Commons/Icons/book.svg';

import { hasPermissions } from '../Commons/CompanyAuth';

// Mismo catalogo fijo que InventoryIntegrationCreate.jsx (asistelibros aca es BA/BE/SA/SE,
// distinto del dominio de Gastos Contables -- ver comentario en ese archivo).
const BooksFlagNames = {
    BA: 'Bienes Afectos',
    BE: 'Bienes Exentos',
    SA: 'Servicios Afectos',
    SE: 'Servicios Exentos',
};

const formatDate = (value) => value ? new Date(value).toLocaleDateString('es-GT') : '';

const formatMoney = (value) =>
    value === null || value === undefined
        ? ''
        : parseFloat(value).toLocaleString('es-GT', { style: 'decimal', minimumFractionDigits: 2 });

// El "id" de este recurso es compuesto ("corre_cbfactura-correlativo", ver comentario en
// Get_Provider_InventoryIntegrations/Providersapi.cs): aunque sp_get_cbfacturainteginv solo
// necesita el correlativo, el id se arma compuesto para poder recuperar la factura sin
// depender de location.state, que no llega hasta aca cuando se entra por rowClick="show"
// desde InventoryIntegrationsList.
const getInvoiceFromId = (id) => {
    const parts = (id || '').split('-');
    return parts.length === 2 ? parts[0] : null;
};

// Detalle de UNA integracion de inventario (sp_get_cbfacturainteginv). Solo lectura, mismo
// patron que AccountingExpenseShow, mas las tablas de Atributos y Números de Serie cuando
// el articulo las tiene (categoria de tipo "ME" con atributos y/o rastreo individual).
const InventoryIntegrationShow = () => {
    const { id } = useParams();
    const isSmall = useMediaQuery(theme => theme.breakpoints.down('sm'));
    const navigate = useNavigate();
    const location = useLocation();
    const dataProvider = useDataProvider();
    const invoiceId = getInvoiceFromId(id);

    const [openAuth, setOpenAuth] = useState(false);
    const [auth, setAuth] = useState(false);
    const [invoiceNumber, setInvoiceNumber] = useState('');
    const [article, setArticle] = useState('');
    const [warehouse, setWarehouse] = useState('');
    const [reason, setReason] = useState('');
    const [quantity, setQuantity] = useState('');
    const [unit, setUnit] = useState('');
    const [amount, setAmount] = useState('');
    const [vatExemptAmount, setVatExemptAmount] = useState('');
    const [isrExemptAmount, setIsrExemptAmount] = useState('');
    const [isrConceptName, setIsrConceptName] = useState('');
    const [booksFlag, setBooksFlag] = useState('');
    const [daiAmount, setDaiAmount] = useState('');
    const [attributes, setAttributes] = useState([]);
    const [serials, setSerials] = useState([]);
    // Mientras esto es true se muestra el spinner de carga inicial (ver el bloque
    // "initialLoading" en el return): esta pantalla necesita traer permisos y el detalle
    // de la integracion de inventario antes de poder mostrarlo, y sin este estado la
    // ventana se veia en blanco (solo el breadcrumb) durante esa espera.
    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                if (!invoiceId) {
                    setAuth(false);
                    setOpenAuth(true);
                    return;
                }

                const authdata = { data: hasPermissions('InventoryIntegration', 'list') };
                if (authdata.data.length > 0) {
                    setAuth(authdata.data[0]);
                    setOpenAuth(!authdata.data[0]);

                    if (authdata.data[0]) {
                        const [{ data }, invoiceData] = await Promise.all([
                            dataProvider.getOne('provider/inventoryintegration', { id }),
                            dataProvider.getOne('provider/invoice', { id: invoiceId }).catch(() => ({ data: {} })),
                        ]);

                        historyTracker.addPath(location.pathname, 'Integración de Inventario', 7);

                        setInvoiceNumber(invoiceData.data?.invoiceNumber || '');
                        setArticle(data.articleName ? `${data.articleId} - ${data.articleName}` : data.articleId);
                        setWarehouse(data.warehouseName || '');
                        setReason(data.reasonName || '');
                        setQuantity(data.quantity != null ? parseFloat(data.quantity).toLocaleString('es-GT', { minimumFractionDigits: 2 }) : '');
                        setUnit(data.unitAbbreviation || '');
                        setAmount(formatMoney(data.amount));
                        setVatExemptAmount(formatMoney(data.vatExemptAmount));
                        setIsrExemptAmount(formatMoney(data.isrExemptAmount));
                        setIsrConceptName(data.isrConceptName || '');
                        setBooksFlag(BooksFlagNames[data.booksFlag] || data.booksFlag || '');
                        setDaiAmount(formatMoney(data.daiAmount));
                        setAttributes(data.attributes || []);
                        setSerials(data.serialNumbers || []);
                    }
                } else {
                    setAuth(false);
                    setOpenAuth(true);
                }
            }
            catch (error) {
                console.log(error);
            }
            finally {
                setInitialLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataProvider, location.pathname, id]);

    const handleRedirectProviderList = () => {
        navigate(`/provider`);
    };

    const handleRedirectInvoice = () => {
        navigate(`/provider/invoice/${invoiceId}/show`);
    };

    const [openProviders, setOpenProviders] = useState(true);

    const handleClickOpenProviders = () => {
        setOpenProviders(!openProviders);
    };

    const menus = [{
        icon: <img src={providers} alt="logo" style={Style.MenuLogo} />, name: 'Proveedores',
        open: openProviders,
        openAction: handleClickOpenProviders,
        actions: [
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Listado', onClick: handleRedirectProviderList },
            { icon: <img src={providerList} alt="logo" style={Style.MenuLogo} />, name: 'Factura', onClick: handleRedirectInvoice },
        ]
    }];

    return (
        <div class="container">
            <BreadCrumbCustom history={historyTracker.getHistory()} />
            {initialLoading ? (
                // Spinner de carga inicial: reutiliza la misma clase "Loading" (index.css) y
                // el mismo CircularProgress que se usa en otras pantallas para la espera de
                // guardado, pero aqui cubre la espera de auth + detalle de la integracion de
                // inventario, que antes dejaba la pantalla en blanco.
                <div class="Loading" style={{ display: 'block' }}>
                    <CircularProgress size="15rem" />
                </div>
            ) : auth ? (
                <div class="edit">
                    {!isSmall ? (
                        <div class="container">
                            <div class="row">
                                <InputFieldCustom readOnly value={invoiceNumber} setValue={setInvoiceNumber} col="col-3" width="95%" label="Factura" type="text" />
                                <InputFieldCustom readOnly value={article} setValue={setArticle} col="col-3" width="95%" label="Artículo" type="text" />
                                <InputFieldCustom readOnly value={warehouse} setValue={setWarehouse} col="col-3" width="95%" label="Almacén" type="text" />
                                <InputFieldCustom readOnly value={reason} setValue={setReason} col="col-3" width="95%" label="Razón" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={quantity} setValue={setQuantity} col="col-3" width="95%" label="Cantidad" type="text" />
                                <InputFieldCustom readOnly value={unit} setValue={setUnit} col="col-3" width="95%" label="Unidad de Medida" type="text" />
                                <InputFieldCustom readOnly value={isrConceptName} setValue={setIsrConceptName} col="col-3" width="95%" label="Concepto ISR" type="text" />
                                <InputFieldCustom readOnly value={booksFlag} setValue={setBooksFlag} col="col-3" width="95%" label="Clasificación" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={amount} setValue={setAmount} col="col-3" width="95%" label="Monto (Q)" type="text" />
                                <InputFieldCustom readOnly value={vatExemptAmount} setValue={setVatExemptAmount} col="col-3" width="95%" label="Monto No Afecto IVA (Q)" type="text" />
                                <InputFieldCustom readOnly value={isrExemptAmount} setValue={setIsrExemptAmount} col="col-3" width="95%" label="Monto No Afecto ISR (Q)" type="text" />
                            </div>
                            <div class="row">
                                <InputFieldCustom readOnly value={daiAmount} setValue={setDaiAmount} col="col-3" width="95%" label="Monto DAI (Q)" type="text" />
                            </div>
                            {attributes.length > 0 ? (
                                <div class="row" style={{ marginTop: '1.5em' }}>
                                    <div class="col-12">
                                        <label style={Style.Label}>Atributos</label>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={Style.DataGrid}>Atributo</TableCell>
                                                    <TableCell style={Style.DataGrid}>Valor</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {attributes.map((item) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{item.label || item.name}</TableCell>
                                                        <TableCell>{item.value}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            ) : null}
                            {serials.length > 0 ? (
                                <div class="row" style={{ marginTop: '1.5em' }}>
                                    <div class="col-12">
                                        <label style={Style.Label}>Números de Serie</label>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell style={Style.DataGrid}>Cantidad</TableCell>
                                                    <TableCell style={Style.DataGrid}>Número de Serie</TableCell>
                                                    <TableCell style={Style.DataGrid}>Fecha de Fabricación</TableCell>
                                                    <TableCell style={Style.DataGrid}>Fecha de Vencimiento</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {serials.map((item) => (
                                                    <TableRow key={item.id}>
                                                        <TableCell>{item.quantity}</TableCell>
                                                        <TableCell>{item.serialNumber}</TableCell>
                                                        <TableCell>{formatDate(item.manufactureDate)}</TableCell>
                                                        <TableCell>{formatDate(item.expirationDate)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </div>
                            ) : null}
                            <div style={{ marginTop: '2em' }}>
                                <BottomToolBar buttons={[]} menu={<InternalMenu menus={menus} />} />
                            </div>
                        </div>
                    ) : (
                        <div class="container">
                        </div>
                    )}
                </div>) : (<AuthNotification openerror={openAuth} severity={'error'} message={'No posee los permisos para poder interactuar con esta vista'} />)
            }
        </div>
    )
}

export default InventoryIntegrationShow;
